import React, { useEffect, useMemo, useState } from 'react';
import { CATEGORY_LABELS, DocumentCategory, DocumentUploadCategory, PropertyDocument } from 'types/Documents';
import TabPanel, { TabPanelItems } from 'components/TabPanel/TabPanel';
import AGMessage from 'components/AGMessage/AGMessage';
import { ModalRef } from 'components/Modal/Modal';
import DocumentList from './DocumentList';
import UploadDocumentModal from './UploadDocumentModal';
import styles from './PropertyDocuments.module.css';

type TabKey = 'all' | DocumentCategory;

type PropertyDocumentsProps = {
    documents: PropertyDocument[];
    categories: DocumentUploadCategory[];
    uploadCategories: DocumentUploadCategory[];
    isLoading: boolean;
    errorMessage: string;
    onUpload: (files: File[], category: DocumentUploadCategory) => Promise<void>;
    uploadModalRef: React.RefObject<ModalRef>;
};

function PropertyDocuments({
    documents,
    categories,
    uploadCategories,
    isLoading,
    errorMessage,
    onUpload,
    uploadModalRef,
}: PropertyDocumentsProps) {
    const [activeTab, setActiveTab] = useState<TabKey>('all');

    const uniqueCategories = useMemo(() => {
        const seen = new Set<DocumentCategory>();
        const result = categories.filter((c) => {
            if (seen.has(c.category)) return false;
            seen.add(c.category);
            return true;
        });
        // Always sort Other to the end
        return result.sort((a, b) => {
            if (a.category === DocumentCategory.OTHER) return 1;
            if (b.category === DocumentCategory.OTHER) return -1;
            return 0;
        });
    }, [categories]);

    const tabItems = useMemo<TabPanelItems<TabKey>>(() => {
        const tabs = [
            {
                title: 'All Documents',
                key: 'all' as TabKey,
                component: <DocumentList documents={documents} isLoading={isLoading} />,
            },
        ];

        for (const cat of uniqueCategories) {
            const catDocuments = documents.filter((d) => d.category?.category === cat.category);
            if (catDocuments.length > 0) {
                tabs.push({
                    title: CATEGORY_LABELS[cat.category],
                    key: cat.category as TabKey,
                    component: <DocumentList documents={catDocuments} isLoading={isLoading} />,
                });
            }
        }

        return tabs;
    }, [documents, uniqueCategories, isLoading]);

    useEffect(() => {
        if (!tabItems.some((t) => t.key === activeTab)) {
            setActiveTab('all' as TabKey);
        }
    }, [tabItems, activeTab]);

    return (
        <div className={styles.container}>
            {errorMessage && <AGMessage color="error" title={errorMessage} />}
            <TabPanel items={tabItems} activeTabKey={activeTab} setActiveTab={setActiveTab} />
            <UploadDocumentModal ref={uploadModalRef} uploadCategories={uploadCategories} onUpload={onUpload} />
        </div>
    );
}

export default PropertyDocuments;
